import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, updateLicenseState } from "../../../store";
import { currentApplication, selectApplicationLicense } from "../../../utils/license";
import { productList } from "../menu-list";
import AppSwitchCurtain from "./app-switch-curtain";
import {
  NAVIGATE_DELAY,
  SWITCH_DURATION,
  SwitchableApp,
} from "./app-switch.types";

interface AppSwitchContextValue {
  switchApp: (app: SwitchableApp) => void;
}

const AppSwitchContext = createContext<AppSwitchContextValue>({
  switchApp: () => undefined,
});

export const useAppSwitch = () => useContext(AppSwitchContext);

function appForPath(pathname: string): SwitchableApp | null {
  return (
    (productList as SwitchableApp[]).find(
      (app) => app.prefix && pathname.includes(`/${app.prefix}`),
    ) || null
  );
}

export function AppSwitchProvider({ children }: { children: ReactNode }) {
  const [switchingTo, setSwitchingTo] = useState<SwitchableApp | null>(null);
  const [switchingFrom, setSwitchingFrom] = useState<SwitchableApp | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { licenseConfig, hardLimit } = useSelector(
    (state: RootState) => state.license,
  );

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const switchApp = useCallback(
    (app: SwitchableApp) => {
      if (!app?.key) return;

      const applicationCode = currentApplication(app.key);
      const selectedLicense = selectApplicationLicense(
        licenseConfig,
        applicationCode,
      );
      dispatch(
        updateLicenseState({
          license:
            selectedLicense?.license_plan_name ||
            selectedLicense?.license_plan_code ||
            null,
          licenseConfig: selectedLicense
            ? {
                ...selectedLicense,
                licenses: licenseConfig?.licenses || [],
                number_of_projects: licenseConfig?.number_of_projects,
              }
            : null,
          hardLimit,
        }),
      );

      clearTimers();
      setSwitchingFrom(appForPath(window.location.pathname));
      setSwitchingTo(app);

      if (app.to) {
        timers.current.push(
          setTimeout(() => navigate(app.to as string), NAVIGATE_DELAY),
        );
      }
      timers.current.push(
        setTimeout(() => {
          setSwitchingTo(null);
          setSwitchingFrom(null);
        }, SWITCH_DURATION),
      );
    },
    [clearTimers, dispatch, hardLimit, licenseConfig, navigate],
  );

  const value = useMemo(() => ({ switchApp }), [switchApp]);

  return (
    <AppSwitchContext.Provider value={value}>
      {children}
      {switchingTo && (
        <AppSwitchCurtain from={switchingFrom} to={switchingTo} />
      )}
    </AppSwitchContext.Provider>
  );
}
